import type { EmptyObject } from "type-fest"

export interface ReferentialCode<T extends string | number = string | number> {
  label: string
  code: T
}

export interface ReferentialIdName<T extends string | number = string | number> {
  id: T
  name: string
}

/** @deprecated Please use ReferentialCode instead  */
export interface ReferentialValue<T extends string | number = string | number> {
  label: string
  value: T
}

/**
 * @description
 * For the Extra options, if you want an optional property, you need to add it like this.
 * Otherwise, you'll lose the optional property
 * @example
 * // Correct
 * { optional : string | undefined, color: string }
 * @example
 * // Wrong
 * { optional ?: string, color: string }
 */
export type ReferentialEnum<Extra extends Record<string, unknown> = EmptyObject> =
  Record<string, { label: string } & Extra>

export function referentialEnumToReferentialCodeList<
  T extends ReferentialEnum<Record<string, unknown>>,
>(enumObj: T) {
  return Object.entries(enumObj).map(([key, stat]) => ({
    code: key as keyof T,
    ...(stat as T[keyof T]),
  }))
}

/**
 * Narrows an untrusted code (URL param, query string...) to a key of a referential enum.
 *
 * Returns `undefined` on any unknown value, so the caller renders an error state
 * instead of letting a meaningless code reach the API or an enum lookup.
 *
 * Usage:
 *   const universeCode = parseReferentialCode(universeEnum, param)
 *   //    ^? UniverseName | undefined
 */
export function parseReferentialCode<
  T extends ReferentialEnum<Record<string, unknown>>,
>(enumObj: T, code: string | undefined): keyof T | undefined {
  return code && Object.hasOwn(enumObj, code) ? (code as keyof T) : undefined
}
